import { useEffect, useState } from "react";
import Shell from "../components/Shell";
import {
  PrimaryButton,
  SecondaryButton,
  Input,
  Toggle,
  KeywordPill,
  Select,
} from "../components/ui-bits";
import {
  fetchSettings,
  updateSettings,
  testTelegram,
  fetchAutoReplies,
  fetchEmailStatus,
  testEmail,
  sendDigestNow,
  fetchGroups,
} from "../lib/api";
import { toast } from "sonner";
import {
  PaperPlaneTiltIcon,
  BellRingingIcon,
  ChatCircleDotsIcon,
  WarningIcon,
  EnvelopeIcon,
  FunnelIcon,
  XIcon,
} from "@phosphor-icons/react";
import { usePush } from "../hooks/usePush";

const RECOMMENDED_AUTOREPLY = `Hi {sender}, I noticed your message in {group} 🚢✈️

I'm Rok Kobal from Europacific — an international freight forwarding & logistics company serving the North Adriatic via the ports of Koper, Rijeka & Trieste and the airports of Ljubljana, Vienna & Zagreb.

We handle sea, road, rail & air freight, customs clearance, warehousing and reliable EXW/DAP door-to-door solutions across Central & Eastern Europe.

Happy to help with rates or routing — just reply here and I'll get back to you shortly.

📧 rok.kobal@europacific.com
🌐 www.europacific.com | www.myeuropacific.com

Kind regards,
Rok Kobal · Europacific`;

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [autoReplies, setAutoReplies] = useState([]);
  const [emailStatus, setEmailStatus] = useState({ configured: false });
  const [groups, setGroups] = useState([]);
  const [newFilterKw, setNewFilterKw] = useState("");
  const push = usePush();

  useEffect(() => {
    fetchSettings().then(setSettings);
    fetchAutoReplies().then(setAutoReplies).catch(() => {});
    fetchEmailStatus().then(setEmailStatus).catch(() => {});
    fetchGroups().then(setGroups).catch(() => {});
  }, []);

  if (!settings) {
    return (
      <Shell title="Settings">
        <div className="text-sm text-zinc-400">Loading…</div>
      </Shell>
    );
  }

  const onSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onTestTelegram = async () => {
    setTesting(true);
    try {
      const r = await testTelegram();
      if (r.sent) toast.success("Test alert sent ✓");
      else toast.error(`Telegram failed: ${r.error}`);
    } finally {
      setTesting(false);
    }
  };

  const set = (patch) => setSettings({ ...settings, ...patch });

  return (
    <Shell title="Settings" subtitle="Score thresholds, AI usage budget, and notifications.">
      {/* App mode */}
      <section className="border-2 border-zinc-950 bg-white p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="overline mb-1">Deployment mode</div>
            <div className="font-heading font-bold text-lg mb-1">
              {settings.app_mode === "corporate" ? "Corporate (multi-user)" : "Personal (single user)"}
            </div>
            <p className="text-sm text-zinc-600 max-w-2xl">
              {settings.app_mode === "corporate"
                ? "Auth is required. Admins invite team members on the Users page. Lead feed and keywords are shared; notification routing is per-user."
                : "No login required. All settings are global. Switch to Corporate to invite team members."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Toggle
              testId="app-mode-toggle"
              checked={settings.app_mode === "corporate"}
              onChange={(v) => set({ app_mode: v ? "corporate" : "personal" })}
              label={settings.app_mode === "corporate" ? "Corporate" : "Personal"}
            />
          </div>
        </div>
        {settings.app_mode === "corporate" && (
          <div className="mt-4 text-xs text-amber-900 bg-amber-50 border border-amber-200 p-3">
            ⚠️ After saving, all dashboard pages will require sign-in. Make sure an admin user exists first (Users page) or use the &quot;Sign in with Microsoft&quot; SSO button.
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Thresholds */}
        <section className="border border-zinc-200 bg-white p-6">
          <div className="overline mb-1">Scoring</div>
          <h3 className="font-heading text-lg font-bold mb-4">Priority thresholds</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Medium ≥</label>
              <Input
                data-testid="settings-medium"
                type="number"
                min={1}
                value={settings.medium_threshold}
                onChange={(e) => set({ medium_threshold: Number(e.target.value) })}
                className="w-32 font-mono-num"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">High ≥</label>
              <Input
                data-testid="settings-high"
                type="number"
                min={1}
                value={settings.high_threshold}
                onChange={(e) => set({ high_threshold: Number(e.target.value) })}
                className="w-32 font-mono-num"
              />
            </div>
            <div className="text-xs text-zinc-500 leading-relaxed border-l-2 border-zinc-200 pl-3">
              Messages scoring under <span className="font-mono-num">{settings.medium_threshold}</span>{" "}
              are ignored (no alert). Between <span className="font-mono-num">{settings.medium_threshold}</span>{" "}
              and <span className="font-mono-num">{settings.high_threshold - 1}</span> trigger a medium alert.
              Anything ≥ <span className="font-mono-num">{settings.high_threshold}</span> triggers a high-priority alert.
            </div>
          </div>
        </section>

        {/* AI */}
        <section className="border border-zinc-200 bg-white p-6">
          <div className="overline mb-1">AI fallback</div>
          <h3 className="font-heading text-lg font-bold mb-4">GPT-4.1-mini classifier</h3>
          <div className="space-y-4">
            <Toggle
              testId="settings-ai-toggle"
              checked={settings.ai_enabled}
              onChange={(v) => set({ ai_enabled: v })}
              label="Enable AI classification for borderline messages"
            />
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Trigger score ≥</label>
                <Input data-testid="settings-ai-trigger" type="number" min={1} value={settings.ai_trigger_score} onChange={(e) => set({ ai_trigger_score: Number(e.target.value) })} className="w-full font-mono-num" />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Max per hour</label>
                <Input data-testid="settings-ai-max" type="number" min={1} value={settings.ai_max_per_hour} onChange={(e) => set({ ai_max_per_hour: Number(e.target.value) })} className="w-full font-mono-num" />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Max per day</label>
                <Input data-testid="settings-ai-max-day" type="number" min={1} value={settings.ai_max_per_day ?? 30} onChange={(e) => set({ ai_max_per_day: Number(e.target.value) })} className="w-full font-mono-num" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Monthly budget (€) — hard ceiling, AI auto-disables when reached</label>
              <Input data-testid="settings-ai-budget" type="number" step="0.50" min={0} value={settings.ai_monthly_budget_eur ?? 5.0} onChange={(e) => set({ ai_monthly_budget_eur: Number(e.target.value) })} className="w-40 font-mono-num" />
            </div>
            <Toggle
              testId="settings-monitor-all"
              checked={settings.monitor_all_groups}
              onChange={(v) => set({ monitor_all_groups: v })}
              label="Force-monitor all groups (override disabled flags)"
            />
          </div>
        </section>

        {/* Auto-reply */}
        <section className="border border-zinc-200 bg-white p-6 lg:col-span-2">
          <div className="overline mb-1">Outbound · Auto-reply</div>
          <h3 className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
            <ChatCircleDotsIcon size={20} weight="bold" /> WhatsApp DM auto-reply
          </h3>

          <div className="flex items-start gap-2 border border-amber-300 bg-amber-50 text-amber-900 p-3 mb-4 text-xs leading-relaxed">
            <WarningIcon size={16} weight="bold" className="flex-shrink-0 mt-0.5" />
            <div>
              <strong>Safety guardrails (always on):</strong> reply is sent as a 1-to-1
              <span className="font-mono-num"> direct message </span> to the sender only,
              <span className="font-mono-num"> never in the group</span>, only on HIGH priority leads,
              with a per-sender cooldown to avoid duplicate outreach. Even so, automated WhatsApp
              messaging carries account-suspension risk — review the message log regularly.
            </div>
          </div>

          <div className="space-y-4">
            <Toggle
              testId="settings-autoreply-toggle"
              checked={!!settings.auto_reply_enabled}
              onChange={(v) => set({ auto_reply_enabled: v })}
              label="Enable auto-reply on HIGH priority leads"
            />

            <div>
              <label className="block text-xs text-zinc-500 mb-1">Cooldown (days per sender)</label>
              <Input
                data-testid="settings-autoreply-cooldown"
                type="number"
                min={1}
                max={365}
                value={settings.auto_reply_cooldown_days ?? 7}
                onChange={(e) => set({ auto_reply_cooldown_days: Number(e.target.value) })}
                className="w-32 font-mono-num"
              />
              <p className="text-xs text-zinc-500 mt-1">
                After replying to a sender, the same sender won't be auto-replied again for this many days.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1 gap-2">
                <label className="block text-xs text-zinc-500">
                  Message template — supported placeholders:{" "}
                  <code className="font-mono-num">{"{sender}"}</code>{" "}
                  <code className="font-mono-num">{"{group}"}</code>{" "}
                  <code className="font-mono-num">{"{keywords}"}</code>
                </label>
                <button
                  type="button"
                  data-testid="settings-autoreply-load-recommended"
                  onClick={() => {
                    set({ auto_reply_template: RECOMMENDED_AUTOREPLY });
                    toast.success("Recommended template loaded — review, then Save");
                  }}
                  className="shrink-0 text-[11px] font-bold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-zinc-50 px-2.5 py-1"
                >
                  Load recommended
                </button>
              </div>
              <textarea
                data-testid="settings-autoreply-template"
                value={settings.auto_reply_template || ""}
                onChange={(e) => set({ auto_reply_template: e.target.value })}
                rows={12}
                placeholder="Hi {sender}, I saw your message about {keywords} in {group}…"
                className="w-full border border-zinc-200 bg-white text-zinc-950 px-3 py-2 text-sm focus:outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 font-mono-num"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Tip: leads often ask "your name?" / "which country?" — including your name,
                email and service scope up front saves a round-trip.
              </p>
            </div>
          </div>

          {/* Recent auto-replies */}
          <div className="mt-6">
            <div className="overline mb-2">Recent auto-replies · {autoReplies.length}</div>
            {autoReplies.length === 0 ? (
              <div className="text-xs text-zinc-400 border border-dashed border-zinc-200 p-4 text-center">
                No auto-replies sent yet.
              </div>
            ) : (
              <div className="border border-zinc-200 max-h-64 overflow-y-auto">
                {autoReplies.slice(0, 20).map((r) => (
                  <div
                    key={r.id}
                    data-testid={`autoreply-row-${r.id}`}
                    className="p-3 border-b border-zinc-100 last:border-b-0 text-xs flex items-start justify-between gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="overline">{r.group_name}</span>
                        <span className="text-zinc-300">·</span>
                        <span className="text-zinc-600 font-mono-num truncate">{r.sender}</span>
                      </div>
                      <p className="text-zinc-700 line-clamp-2 break-words">{r.text}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {r.status === "sent" ? (
                        <span className="text-emerald-600 font-medium">SENT</span>
                      ) : r.status === "failed" ? (
                        <span className="text-[#FF3B30] font-medium" title={r.error}>FAILED</span>
                      ) : (
                        <span className="text-zinc-500 font-medium">PENDING</span>
                      )}
                      <div className="font-mono-num text-[10px] text-zinc-400 mt-1">
                        {(r.sent_at || "").replace("T", " ").split(".")[0]}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Pushover */}
        <section className="border border-zinc-200 bg-white p-6 lg:col-span-2">
          <div className="overline mb-1">Notifications · Channel 1</div>
          <h3 className="font-heading text-lg font-bold mb-2">Pushover (mobile app)</h3>
          <p className="text-sm text-zinc-500 mb-4">
            Install the <a href="https://pushover.net/clients" target="_blank" rel="noreferrer" className="underline hover:text-zinc-950">Pushover</a> app on
            your iPhone/iPad/Android (free trial, $5 one-time per platform). Configure{" "}
            <code className="font-mono-num text-xs bg-zinc-100 px-1.5 py-0.5">PUSHOVER_USER_KEY</code>{" "}
            and{" "}
            <code className="font-mono-num text-xs bg-zinc-100 px-1.5 py-0.5">PUSHOVER_APP_TOKEN</code>{" "}
            in <code className="font-mono-num text-xs bg-zinc-100 px-1.5 py-0.5">backend/.env</code> and restart backend.
            HIGH leads play the <em>siren</em> sound and bypass quiet hours.
          </p>
          <ol className="text-xs text-zinc-600 list-decimal pl-5 mb-4 space-y-1">
            <li>Sign up at <a href="https://pushover.net" target="_blank" rel="noreferrer" className="underline">pushover.net</a> → your <strong>User Key</strong> is on the dashboard</li>
            <li>Go to <em>Your Applications</em> → <em>Create an Application/API Token</em> → name it <code className="font-mono-num">CARGO/SCAN</code> → copy the <strong>API Token/Key</strong></li>
            <li>Install the Pushover mobile app and sign in with the same account</li>
          </ol>
          <SecondaryButton
            data-testid="settings-telegram-test"
            onClick={onTestTelegram}
            disabled={testing}
            className="flex items-center gap-2"
          >
            <PaperPlaneTiltIcon size={16} />
            {testing ? "Sending…" : "Send test alert"}
          </SecondaryButton>
        </section>

        {/* Email (M365 Graph) */}
        <section className="border border-zinc-200 bg-white p-6 lg:col-span-2">
          <div className="overline mb-1">Notifications · Channel 3</div>
          <h3 className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
            <EnvelopeIcon size={20} weight="bold" /> Email (Microsoft 365 / Entra)
          </h3>

          {!emailStatus.configured && (
            <div className="text-xs text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-3 mb-4">
              Azure credentials missing in <code className="font-mono-num">backend/.env</code>. Set{" "}
              <code className="font-mono-num">AZURE_TENANT_ID</code>,{" "}
              <code className="font-mono-num">AZURE_CLIENT_ID</code>,{" "}
              <code className="font-mono-num">AZURE_CLIENT_SECRET</code>,{" "}
              <code className="font-mono-num">AZURE_MAILBOX_USER_ID</code>, then restart the backend.
              See README for the Azure portal setup steps.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-4">
                <Toggle
                  testId="email-master-toggle"
                  checked={!!settings.email_enabled}
                  onChange={(v) => set({ email_enabled: v })}
                  label="Enable email notifications"
                />
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Destination email (yours)</label>
                  <Input
                    data-testid="email-destination"
                    type="email"
                    value={settings.email_alert_to || ""}
                    onChange={(e) => set({ email_alert_to: e.target.value })}
                    placeholder="you@yourdomain.com"
                    className="w-full"
                  />
                </div>
                <Toggle
                  testId="email-alert-toggle"
                  checked={!!settings.email_alert_on_high}
                  onChange={(v) => set({ email_alert_on_high: v })}
                  label="Real-time email on every HIGH lead"
                />
              </div>
            </div>

            <div>
              <div className="overline mb-2">Digest (lower-priority round-up)</div>
              <div className="space-y-4">
                <Toggle
                  testId="email-digest-toggle"
                  checked={!!settings.email_digest_enabled}
                  onChange={(v) => set({ email_digest_enabled: v })}
                  label="Enable digest email"
                />
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-zinc-500 mb-1">Frequency</label>
                    <Select
                      data-testid="email-digest-freq"
                      value={settings.email_digest_frequency || "daily"}
                      onChange={(e) => set({ email_digest_frequency: e.target.value })}
                      className="w-full"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </Select>
                  </div>
                  <div className="w-28">
                    <label className="block text-xs text-zinc-500 mb-1">Hour (UTC)</label>
                    <Input
                      data-testid="email-digest-hour"
                      type="number"
                      min={0}
                      max={23}
                      value={settings.email_digest_hour_utc ?? 6}
                      onChange={(e) => set({ email_digest_hour_utc: Number(e.target.value) })}
                      className="w-full font-mono-num"
                    />
                  </div>
                  <div className="w-28">
                    <label className="block text-xs text-zinc-500 mb-1">Min score</label>
                    <Input
                      data-testid="email-digest-min"
                      type="number"
                      min={0}
                      value={settings.email_digest_min_score ?? 1}
                      onChange={(e) => set({ email_digest_min_score: Number(e.target.value) })}
                      className="w-full font-mono-num"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-6">
            <SecondaryButton
              data-testid="email-test-btn"
              onClick={async () => {
                const r = await testEmail(settings.email_alert_to);
                if (r.sent) toast.success(`Test email sent to ${r.to}`);
                else toast.error(r.error || "Failed");
              }}
              disabled={!emailStatus.configured}
              className="flex items-center gap-2"
            >
              <PaperPlaneTiltIcon size={16} />
              Send test email
            </SecondaryButton>
            <SecondaryButton
              data-testid="email-digest-now-btn"
              onClick={async () => {
                const r = await sendDigestNow();
                if (r.sent) toast.success(`Digest sent (${r.count} leads)`);
                else toast.error(r.error || r.reason || "Failed");
              }}
              disabled={!emailStatus.configured}
              className="flex items-center gap-2"
            >
              <PaperPlaneTiltIcon size={16} />
              Send digest now
            </SecondaryButton>
          </div>
        </section>

        {/* Web Push */}
        <section className="border border-zinc-200 bg-white p-6 lg:col-span-2">
          <div className="overline mb-1">Notifications · Channel 2</div>
          <h3 className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
            <BellRingingIcon size={20} weight="bold" /> Web Push (PWA)
          </h3>
          <p className="text-sm text-zinc-500 mb-4">
            Get a native phone/desktop notification on every <span className="font-bold text-[#FF3B30]">HIGH</span> priority
            lead — even when the app is closed. Install CARGO/SCAN to your home screen first,
            then enable push below. Works alongside Telegram for redundant alerting.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="border border-zinc-200 p-3">
              <div className="overline">Browser support</div>
              <div className="text-sm mt-1 font-medium">
                {push.supported ? (
                  <span className="text-emerald-600">✓ Supported</span>
                ) : (
                  <span className="text-[#FF3B30]">✗ Not supported</span>
                )}
              </div>
            </div>
            <div className="border border-zinc-200 p-3">
              <div className="overline">Permission</div>
              <div className="text-sm mt-1 font-medium font-mono-num">
                {push.permission}
              </div>
            </div>
            <div className="border border-zinc-200 p-3">
              <div className="overline">Subscription</div>
              <div className="text-sm mt-1 font-medium">
                {push.subscribed ? (
                  <span className="text-emerald-600">✓ Active</span>
                ) : (
                  <span className="text-zinc-500">— Off</span>
                )}
              </div>
            </div>
          </div>

          {!push.serverConfigured && (
            <div className="mb-3 text-xs text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-3">
              Server VAPID keys missing — push won't work. Set{" "}
              <code className="font-mono-num">VAPID_PUBLIC_KEY</code> and{" "}
              <code className="font-mono-num">VAPID_PRIVATE_PEM_B64</code> in{" "}
              <code className="font-mono-num">backend/.env</code> and restart the backend.
            </div>
          )}

          {push.error && (
            <div className="mb-3 text-xs text-[#FF3B30]">{push.error}</div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {!push.subscribed ? (
              <PrimaryButton
                data-testid="push-enable-btn"
                onClick={async () => {
                  const ok = await push.subscribe();
                  if (ok) toast.success("Push notifications enabled ✓");
                  else if (push.error) toast.error(push.error);
                }}
                disabled={!push.supported || !push.serverConfigured || push.working}
                className="flex items-center gap-2"
              >
                <BellRingingIcon size={16} weight="bold" />
                {push.working ? "Enabling…" : "Enable push on this device"}
              </PrimaryButton>
            ) : (
              <SecondaryButton
                data-testid="push-disable-btn"
                onClick={async () => {
                  await push.unsubscribe();
                  toast.success("Push disabled on this device");
                }}
                disabled={push.working}
              >
                {push.working ? "Working…" : "Disable push on this device"}
              </SecondaryButton>
            )}

            <SecondaryButton
              data-testid="push-test-btn"
              onClick={async () => {
                const r = await push.sendTest();
                if (r?.error) toast.error(r.error);
                else if ((r?.sent ?? 0) === 0)
                  toast.error("No active subscribers (enable push on this device first).");
                else toast.success(`Sent to ${r.sent} subscriber(s)`);
              }}
              disabled={!push.serverConfigured || push.working}
              className="flex items-center gap-2"
            >
              <PaperPlaneTiltIcon size={16} />
              Send test push
            </SecondaryButton>
          </div>

          <p className="text-[11px] text-zinc-400 mt-4 leading-relaxed">
            iOS note: web push requires iOS 16.4+ and the PWA must be installed via{" "}
            <em>Add to Home Screen</em> first. Permissions are device-specific —
            enable on each device you want notified.
          </p>
        </section>

        {/* Push filters (per-route rules) */}
        <section className="border border-zinc-200 bg-white p-6 lg:col-span-2">
          <div className="overline mb-1">Notifications · Channel 2 · Smart routing</div>
          <h3 className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
            <FunnelIcon size={20} weight="bold" /> Per-route push rules
          </h3>
          <p className="text-sm text-zinc-500 mb-4">
            By default, every HIGH-priority lead pushes to your phone. Add filters here to receive
            push notifications <strong>only</strong> for leads matching specific keywords (e.g.{" "}
            <code className="font-mono-num">koper</code>, <code className="font-mono-num">munich</code>) or
            specific groups. Leave both empty for default behaviour (push on all HIGH). Telegram and
            email alerts are <strong>not</strong> filtered.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="overline mb-2">Keyword filter</div>
              <div className="flex flex-wrap gap-2 mb-3 min-h-[28px]">
                {(settings.push_filter_keywords || []).length === 0 ? (
                  <span className="text-xs text-zinc-400">No filter — push on every HIGH</span>
                ) : (
                  settings.push_filter_keywords.map((k) => (
                    <span
                      key={k}
                      data-testid={`pushfilter-kw-${k}`}
                      className="inline-flex items-center gap-1 bg-zinc-900 text-white px-2 py-1 text-xs font-mono-num"
                    >
                      {k}
                      <button
                        type="button"
                        onClick={() =>
                          set({
                            push_filter_keywords: settings.push_filter_keywords.filter((x) => x !== k),
                          })
                        }
                        className="hover:text-[#FF3B30]"
                        aria-label="Remove"
                      >
                        <XIcon size={12} weight="bold" />
                      </button>
                    </span>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="pushfilter-input"
                  value={newFilterKw}
                  onChange={(e) => setNewFilterKw(e.target.value)}
                  placeholder="e.g. koper"
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const v = newFilterKw.trim().toLowerCase();
                      if (v && !(settings.push_filter_keywords || []).includes(v)) {
                        set({ push_filter_keywords: [...(settings.push_filter_keywords || []), v] });
                        setNewFilterKw("");
                      }
                    }
                  }}
                />
                <SecondaryButton
                  data-testid="pushfilter-add"
                  onClick={() => {
                    const v = newFilterKw.trim().toLowerCase();
                    if (v && !(settings.push_filter_keywords || []).includes(v)) {
                      set({ push_filter_keywords: [...(settings.push_filter_keywords || []), v] });
                      setNewFilterKw("");
                    }
                  }}
                >
                  Add
                </SecondaryButton>
              </div>
            </div>

            <div>
              <div className="overline mb-2">Group filter</div>
              <p className="text-xs text-zinc-500 mb-2">
                Tick the groups whose HIGH leads should push. Leave all unticked to push on every HIGH.
              </p>
              <div className="max-h-48 overflow-y-auto border border-zinc-200">
                {groups.length === 0 ? (
                  <div className="p-3 text-xs text-zinc-400">No groups registered yet.</div>
                ) : (
                  groups.map((g) => {
                    const checked = (settings.push_filter_groups || []).includes(g.group_id);
                    return (
                      <label
                        key={g.id}
                        className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 cursor-pointer text-sm"
                      >
                        <input
                          type="checkbox"
                          data-testid={`pushfilter-group-${g.id}`}
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...(settings.push_filter_groups || []), g.group_id]
                              : (settings.push_filter_groups || []).filter((x) => x !== g.group_id);
                            set({ push_filter_groups: next });
                          }}
                        />
                        <span className="truncate">{g.group_name}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="flex justify-end mt-8">
        <PrimaryButton data-testid="settings-save" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save settings"}
        </PrimaryButton>
      </div>
    </Shell>
  );
}
